import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Check, ChevronDown, Download, Edit3, Ellipsis, Eye, Filter, Plus, Trash2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import QuotationReferencesCell from "../components/quotation/QuotationReferencesCell";
import QuotationAttachmentControl from "../components/quotation/QuotationAttachmentControl";
import QuotationLifecycleDialog from "../components/quotation/QuotationLifecycleDialog";
import ServiceCostPresentation from "../components/service/ServiceCostPresentation";
import { Button } from "../components/ui/button";
import { DatasetSelectorGroup, DatasetSelectorOption } from "../components/ui/dataset-selector";
import {
  DataValue,
  PassiveBadge,
  PrimarySecondaryValue,
  formatPresentationDate,
  formatPresentationTimestamp,
} from "../components/ui/data-presentation";
import {
  ListControls,
  ListPagination,
  ListSelectorRow,
  ListErrorNotice,
  ListTable,
  ListTableRefreshingRow,
  ListTableShell,
  ListTableState,
  ListTableViewport,
} from "../components/ui/list-table";
import {
  ClearAllFilters,
  ColumnDateFilter,
  ColumnFilterCell,
  ColumnFilterRow,
  ColumnPriceRangeFilter,
  ColumnSelectFilter,
  ColumnTextFilter,
  DateRangeSelector,
  GlobalSearch,
} from "../components/ui/search-filter";
import { OrdinaryPageBody, OrdinaryPageHeader } from "../components/ui/page-structure";
import { RefreshButton } from "../components/ui/refresh-button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { formatApiError } from "../lib/api";
import { deleteQuotation, downloadQuotationPdf, generateQuotationPdf, listDeletedQuotations, listQuotationCreators, listQuotationHistory } from "../lib/quotationApi";
import { nextQuotationSort, QUOTATION_DEFAULTS, QUOTATION_TYPE_BADGE_CLASSES, readQuotationHistoryState, writeQuotationHistoryState } from "../lib/quotationHistory";

const BRANCHES = ["jakarta", "surabaya", "bali"];
const BRANCH_TAB_CLASSES = {
  jakarta: "coral",
  surabaya: "sage",
  bali: "powder-blue",
};
const QUOTATION_TABLE_GEOMETRY = Object.freeze([
  { key: "type", kind: "fixed", width: 82 },
  { key: "quotation_number", kind: "fixed", width: 190 },
  { key: "date", kind: "fixed", width: 150 },
  { key: "customer", kind: "fill", minWidth: 220 },
  { key: "references", kind: "fixed", width: 300 },
  { key: "service_cost", kind: "fixed", width: 180 },
  { key: "created_by", kind: "fixed", width: 210 },
  { key: "actions", kind: "utility", width: 120 },
]);
const DELETED_QUOTATION_TABLE_GEOMETRY = Object.freeze([
  ...QUOTATION_TABLE_GEOMETRY.slice(0, -1),
  { key: "deleted_at_by", kind: "fixed", width: 190 },
  { key: "delete_reason", kind: "fixed", width: 240 },
  QUOTATION_TABLE_GEOMETRY.at(-1),
]);
function formatDate(value) {
  return formatPresentationDate(value);
}

function formatDateTime(value) {
  return formatPresentationTimestamp(value);
}

function SortHeading({ field, label, state, onSort }) {
  const active = state.sort_by === field;
  return <button type="button" className="inline-flex items-center gap-1 text-left hover:text-primary" onClick={() => onSort(field)} aria-label={`Sort by ${label}`}>{label}{active ? state.sort_order === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" /> : null}</button>;
}

function SortableTableHead({ field, label, state, onSort }) {
  const active = state.sort_by === field;
  return (
    <th aria-sort={active ? (state.sort_order === "asc" ? "ascending" : "descending") : "none"}>
      <SortHeading field={field} label={label} state={state} onSort={onSort} />
    </th>
  );
}

function QuotationRowActions({ quotation, deleted, isAdmin, generating, onView, onGenerate, onEdit, onDelete }) {
  const canGenerate = quotation.permissions?.generate === true;
  const canEdit = !deleted && Boolean(quotation.permissions?.edit);
  const canDelete = !deleted && isAdmin;
  const actionCount = 1 + Number(canGenerate) + Number(canEdit) + Number(canDelete);
  const usesMoreActions = actionCount >= 3;
  const generateLabel = generating ? "Generating..." : "Generate Quotation";

  const generateAction = (
    <Button
      type="button"
      variant="export"
      size="row-icon"
      disabled={generating}
      aria-busy={generating || undefined}
      aria-label={generateLabel}
      title={generateLabel}
      onClick={onGenerate}
      data-quotation-row-action="generate"
    >
      <Download />
      <span className="sr-only">{generateLabel}</span>
    </Button>
  );

  return (
    <div className="flex gap-2">
      <Button type="button" variant="info" size="row-icon" title="View quotation" aria-label={`View ${quotation.quotation_number}`} onClick={onView} data-quotation-row-action="view"><Eye /></Button>
      {!usesMoreActions && canGenerate ? generateAction : null}
      {!usesMoreActions && canEdit ? <Button type="button" variant="edit" size="row-icon" title="Edit quotation" aria-label={`Edit ${quotation.quotation_number}`} onClick={onEdit} data-quotation-row-action="edit"><Edit3 /></Button> : null}
      {!usesMoreActions && canDelete ? <Button type="button" variant="transfer-remove" size="row-icon" title="Delete quotation" aria-label={`Delete ${quotation.quotation_number}`} onClick={onDelete} data-quotation-row-action="delete"><Trash2 /></Button> : null}
      {usesMoreActions ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="neutral" size="row-icon" title="More actions" aria-label={`More actions for ${quotation.quotation_number}`} data-quotation-row-action="more"><Ellipsis /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canGenerate ? <DropdownMenuItem data-action-variant="export" disabled={generating} aria-busy={generating || undefined} onSelect={onGenerate}><Download />{generateLabel}</DropdownMenuItem> : null}
            {canEdit ? <DropdownMenuItem onSelect={onEdit}><Edit3 />Edit</DropdownMenuItem> : null}
            {canDelete ? <DropdownMenuItem variant="destructive" data-action-category="destructive" onSelect={onDelete}><Trash2 />Delete</DropdownMenuItem> : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}

export default function QuotationHistory({ deleted = false }) {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const applied = useMemo(() => readQuotationHistoryState(searchParams), [searchParams]);
  const [draft, setDraft] = useState(applied);
  const [result, setResult] = useState({ data: [], pagination: { total: 0, total_pages: 0 } });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [creators, setCreators] = useState([]);
  const [creatorsLoading, setCreatorsLoading] = useState(true);
  const [creatorsError, setCreatorsError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [generatingIds, setGeneratingIds] = useState(() => new Set());
  const requestId = useRef(0);
  const generateInFlight = useRef(new Set());

  useEffect(() => setDraft(applied), [applied]);
  useEffect(() => {
    const controller = new AbortController();
    listQuotationCreators({ signal: controller.signal }).then((items) => setCreators(items)).catch((requestError) => {
      if (requestError?.code !== "ERR_CANCELED") setCreatorsError("Creator options are unavailable. Unfiltered History is still available.");
    }).finally(() => setCreatorsLoading(false));
    return () => controller.abort();
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    const id = ++requestId.current;
    if (result.data.length) setRefreshing(true); else setLoading(true);
    setError("");
    const loadHistory = deleted ? listDeletedQuotations : listQuotationHistory;
    loadHistory(applied, { signal: controller.signal }).then((response) => {
      if (id !== requestId.current) return;
      const pagination = response?.pagination || {};
      if (pagination.total_pages > 0 && applied.page > pagination.total_pages) {
        setSearchParams(writeQuotationHistoryState({ ...applied, page: pagination.total_pages }), { replace: true });
        return;
      }
      setResult({ data: Array.isArray(response?.data) ? response.data : [], pagination });
    }).catch((requestError) => {
      if (id === requestId.current && requestError?.code !== "ERR_CANCELED") setError(formatApiError(requestError));
    }).finally(() => { if (id === requestId.current) { setLoading(false); setRefreshing(false); } });
    return () => controller.abort();
  }, [deleted, searchParams, retryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyState = (state, options) => setSearchParams(writeQuotationHistoryState(state), options);
  const updateDraft = (name, value) => setDraft((current) => ({ ...current, [name]: value }));
  const applyFilter = (name, value) => applyState({ ...applied, [name]: value, page: 1 });
  const applyExactDate = (value) => applyState({
    ...applied,
    date_exact: value,
    page: 1,
  });
  const clear = () => {
    const value = { ...QUOTATION_DEFAULTS, branch: applied.branch, sort_by: applied.sort_by, sort_order: applied.sort_order, page_size: applied.page_size };
    setDraft(value); applyState(value);
  };
  const filtered = Boolean(
    applied.search
    || applied.quotation_type
    || applied.quotation_number
    || applied.customer_name
    || applied.reference
    || applied.service_cost_min
    || applied.service_cost_max
    || applied.created_by
    || applied.date_from
    || applied.date_to
    || applied.date_exact
  );
  const totalPages = result.pagination?.total_pages || 0;
  const historyPath = deleted ? "/quotations/deleted" : "/quotations";
  const historyReturn = `${historyPath}${searchParams.toString() ? `?${searchParams}` : ""}`;
  const openQuotation = (id, edit = false) => navigate(`${deleted ? "/quotations/deleted" : "/quotations"}/${encodeURIComponent(id)}${edit ? "/edit" : ""}?return=${encodeURIComponent(historyReturn)}`);
  const creatorOptions = creators.map((creator) => ({ value: creator.id, label: `${creator.name} — ${creator.email}` }));
  const isAdmin = user?.role === "admin";
  const columnCount = deleted ? 10 : 8;
  const confirmDelete = async (reason) => {
    if (!deleteTarget) return;
    try {
      await deleteQuotation(deleteTarget.id, reason);
      const quotationNumber = deleteTarget.quotation_number;
      setDeleteTarget(null);
      setDeleteError("");
      toast.success(quotationNumber ? `Quotation ${quotationNumber} deleted successfully.` : "Quotation deleted successfully.");
      setRetryKey((value) => value + 1);
    } catch (requestError) {
      setDeleteError(formatApiError(requestError));
      throw requestError;
    }
  };
  const generate = async (quotation) => {
    if (generateInFlight.current.has(quotation.id)) return;
    generateInFlight.current.add(quotation.id);
    setGeneratingIds(new Set(generateInFlight.current));
    try {
      downloadQuotationPdf(await generateQuotationPdf(quotation.id));
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      generateInFlight.current.delete(quotation.id);
      setGeneratingIds(new Set(generateInFlight.current));
    }
  };

  return <OrdinaryPageBody data-testid={deleted ? "deleted-quotation-history" : "quotation-history"}>
    <OrdinaryPageHeader
      title={deleted ? "Deleted Quotations" : "Quotation History"}
      actions={deleted ? <RefreshButton onClick={() => setRetryKey((value) => value + 1)} loading={refreshing} accessibleLabel="Refresh deleted quotations" data-testid="deleted-quotation-refresh" /> : <><QuotationAttachmentControl isAdmin={isAdmin} /><Button onClick={() => navigate("/quotations/create", { state: { quotationHistoryReturn: historyReturn } })} data-testid="quotation-create-action"><Plus />Create Quotation</Button></>}
      data-testid="quotation-history-page-header"
    />
    <ListTableShell data-testid="quotation-list-shell">
    <ListControls className="gap-0" data-testid="quotation-dataset-controls">
    <ListSelectorRow className="justify-between gap-x-6 xl:flex-nowrap" aria-label="Quotation dataset selectors">
      <DatasetSelectorGroup aria-label="Quotation Branch">
        {BRANCHES.map((branch) => {
          const active = applied.branch === branch;
          return <DatasetSelectorOption key={branch} selected={active} accent={BRANCH_TAB_CLASSES[branch]} className="w-[172px] max-w-[172px]" onClick={() => applyState({ ...applied, branch, page: 1 })} data-testid={`quotation-branch-${branch}`}>{branch}</DatasetSelectorOption>;
        })}
      </DatasetSelectorGroup>
      <div className="mb-2 flex w-full flex-col gap-2 sm:flex-row sm:items-stretch xl:ml-auto xl:w-auto">
      {isAdmin || deleted ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="neutral"
              className="h-9 min-w-[168px] shrink-0 justify-between rounded-sm border-input bg-card px-3 text-foreground shadow-none hover:border-input hover:bg-card hover:text-foreground focus-visible:border-ring focus-visible:ring-ring/20 focus-visible:ring-offset-0"
              aria-label={`Select Quotation History dataset: ${deleted ? "Deleted" : "Active"}`}
              data-testid="quotation-lifecycle-selector"
            >
              <Filter aria-hidden="true" />
              <span className="flex-1 text-left">{deleted ? "Deleted" : "Active"}</span>
              <ChevronDown className="text-zinc-400" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[168px]" data-testid="quotation-lifecycle-menu">
            {[
              ["active", "Active"],
              ["deleted", "Deleted"],
            ].map(([value, label]) => {
              const selected = deleted ? value === "deleted" : value === "active";
              return <DropdownMenuItem key={value} role="menuitemradio" aria-checked={selected} onSelect={() => { if (!selected) navigate(`${value === "deleted" ? "/quotations/deleted" : "/quotations"}${searchParams.toString() ? `?${searchParams}` : ""}`); }} className="justify-between rounded-[var(--action-radius-compact)]" data-testid={`quotation-lifecycle-${value}`}><span>{label}</span>{selected ? <Check className="text-[var(--neon-cyan-outline-foreground)]" aria-hidden="true" /> : null}</DropdownMenuItem>;
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <div className="w-[170px] shrink-0" data-testid="quotation-date-range">
        <DateRangeSelector
          value={{ from: draft.date_from, to: draft.date_to }}
          onChange={({ from, to }) => setDraft((current) => ({ ...current, date_from: from, date_to: to }))}
          onApply={({ from, to }) => applyState({ ...applied, date_from: from, date_to: to, page: 1 })}
          mode="immediate"
          label={null}
          aria-label="Quotation Date Range"
          controlsClassName="h-9"
          id="quotation-date-range"
        />
      </div>
      <GlobalSearch
        value={draft.search}
        onValueChange={(value) => updateDraft("search", value)}
        onApply={(value) => applyFilter("search", value)}
        placeholder="Global Search"
        inputProps={{ maxLength: 200, "aria-label": "Global Search", "data-testid": "quotation-global-search" }}
      />
      </div>
    </ListSelectorRow>
    {creatorsError ? <div className="px-5 pb-3 text-xs text-warning" role="status">{creatorsError}</div> : null}
    </ListControls>
      {error && result.data.length ? (
        <ListErrorNotice onRetry={() => setRetryKey((value) => value + 1)} testId="quotation-refresh-error">
          {error}
        </ListErrorNotice>
      ) : null}
      <ListTableViewport>
        <ListTable geometry={deleted ? DELETED_QUOTATION_TABLE_GEOMETRY : QUOTATION_TABLE_GEOMETRY} data-testid="quotation-history-table">
          <thead><tr>
            <th>Type</th>
            <SortableTableHead field="quotation_number" label="Quotation Number" state={applied} onSort={(field) => applyState(nextQuotationSort(applied, field))} />
            <SortableTableHead field="date" label="Date" state={applied} onSort={(field) => applyState(nextQuotationSort(applied, field))} />
            <SortableTableHead field="customer" label="Customer" state={applied} onSort={(field) => applyState(nextQuotationSort(applied, field))} />
            <th>Service Tickets / SN</th>
            <SortableTableHead field="grand_total" label="Service Cost" state={applied} onSort={(field) => applyState(nextQuotationSort(applied, field))} />
            <SortableTableHead field="created_by" label="Created By" state={applied} onSort={(field) => applyState(nextQuotationSort(applied, field))} />
            {deleted ? <><th>Deleted At / By</th><th>Delete Reason</th></> : null}
            <th>Actions</th>
          </tr>
          <ColumnFilterRow data-testid="quotation-column-filter-row">
            <ColumnFilterCell><ColumnSelectFilter value={draft.quotation_type} onValueChange={(value) => updateDraft("quotation_type", value)} onApply={(value) => applyFilter("quotation_type", value)} options={[{ value: "sq", label: "SQ" }, { value: "mq", label: "MQ" }, { value: "cq", label: "CQ" }]} placeholder="All" aria-label="Filter Type" /></ColumnFilterCell>
            <ColumnFilterCell><ColumnTextFilter value={draft.quotation_number} onValueChange={(value) => updateDraft("quotation_number", value)} onApply={(value) => applyFilter("quotation_number", value)} placeholder="Filter…" aria-label="Filter Quotation Number" maxLength={200} /></ColumnFilterCell>
            <ColumnFilterCell><ColumnDateFilter value={draft.date_exact} onValueChange={(value) => updateDraft("date_exact", value)} onApply={applyExactDate} aria-label="Filter Date" /></ColumnFilterCell>
            <ColumnFilterCell><ColumnTextFilter value={draft.customer_name} onValueChange={(value) => updateDraft("customer_name", value)} onApply={(value) => applyFilter("customer_name", value)} placeholder="Filter…" aria-label="Filter Customer" maxLength={200} /></ColumnFilterCell>
            <ColumnFilterCell><ColumnTextFilter value={draft.reference} onValueChange={(value) => updateDraft("reference", value)} onApply={(value) => applyFilter("reference", value)} placeholder="Filter…" aria-label="Filter Service Tickets or Serial Number" maxLength={200} /></ColumnFilterCell>
            <ColumnFilterCell><ColumnPriceRangeFilter value={{ min: draft.service_cost_min, max: draft.service_cost_max }} onValueChange={({ min, max }) => setDraft((current) => ({ ...current, service_cost_min: min, service_cost_max: max }))} onApply={({ min, max }) => applyState({ ...applied, service_cost_min: min, service_cost_max: max, page: 1 })} aria-label="Service Cost range" /></ColumnFilterCell>
            <ColumnFilterCell><ColumnSelectFilter value={draft.created_by} onValueChange={(value) => updateDraft("created_by", value)} onApply={(value) => applyFilter("created_by", value)} options={creatorOptions} placeholder={creatorsLoading ? "Loading…" : "All Users"} disabled={creatorsLoading} aria-label="Filter Created By" data-testid="quotation-creator-select" /></ColumnFilterCell>
            {deleted ? <><ColumnFilterCell aria-label="Deleted At and By filter" /><ColumnFilterCell aria-label="Delete Reason filter" /></> : null}
            <ColumnFilterCell className="align-middle" aria-label="Actions filter controls"><div className="flex h-7 w-full items-center justify-end"><ClearAllFilters onClick={clear} disabled={!filtered} data-testid="quotation-filter-clear-action" /></div></ColumnFilterCell>
          </ColumnFilterRow></thead>
      <tbody>
        <ListTableRefreshingRow active={refreshing} colSpan={columnCount} testId="quotation-refreshing" />
        {loading && !result.data.length ? <ListTableState colSpan={columnCount} kind="loading" className="py-12">Loading {deleted ? "deleted quotations" : "quotation History"}...</ListTableState> : error && !result.data.length ? <ListTableState colSpan={columnCount} kind="error" onRetry={() => setRetryKey((value) => value + 1)}>{error}</ListTableState> : !result.data.length ? <ListTableState colSpan={columnCount} kind={filtered ? "filtered-empty" : "empty"} className="py-12"><span className="font-medium">{filtered ? "No quotations match these filters." : deleted ? "No deleted quotations." : "No saved quotations."}</span></ListTableState> : result.data.map((quotation) => <tr key={quotation.id}>
        <td><PassiveBadge tone="custom" className={`uppercase ${QUOTATION_TYPE_BADGE_CLASSES[quotation.quotation_type] || ""}`}>{quotation.quotation_type}</PassiveBadge></td>
        <td><button className="max-w-[220px] break-all text-left font-semibold text-primary hover:underline" title={quotation.quotation_number} onClick={() => openQuotation(quotation.id)}>{quotation.quotation_number}</button></td><td className="whitespace-nowrap tabular-nums">{formatDate(quotation.date)}</td><td className="min-w-0"><DataValue value={quotation.customer_name} containment="truncate" className="block max-w-full" /></td><td><QuotationReferencesCell mode={quotation.quotation_type} references={quotation.references || []} /></td>
        <td className="whitespace-nowrap"><ServiceCostPresentation serviceCost={{ final_service_cost: quotation.quotation_type === "mq" ? quotation.service_cost_total : quotation.final_service_cost ?? quotation.final_cost, gross_service_cost: quotation.gross_service_cost ?? quotation.gross_cost ?? quotation.service_cost_total, effective_discount_percentage: quotation.effective_discount_percentage }} compact quotationHistory /></td>
        <td><PrimarySecondaryValue primary={quotation.created_by?.name} secondary={quotation.created_by?.email} className="max-w-[180px]" secondaryContainment="truncate" /></td>{deleted ? <><td><span className="block whitespace-nowrap text-xs">{formatDateTime(quotation.deleted_at)}</span><DataValue value={quotation.deleted_by?.name || quotation.deleted_by?.email} containment="truncate" className="block max-w-[180px] text-[11px] text-muted-foreground" /></td><td className="max-w-[260px] text-xs"><DataValue value={quotation.delete_reason} /></td></> : null}<td><QuotationRowActions quotation={quotation} deleted={deleted} isAdmin={isAdmin} generating={generatingIds.has(quotation.id)} onView={() => openQuotation(quotation.id)} onGenerate={() => generate(quotation)} onEdit={() => openQuotation(quotation.id, true)} onDelete={() => { setDeleteError(""); setDeleteTarget(quotation); }} /></td>
      </tr>)}</tbody>
        </ListTable>
      </ListTableViewport>
      <ListPagination
        page={applied.page}
        pageSize={applied.page_size}
        total={result.pagination?.total || 0}
        totalPages={totalPages}
        loading={loading || refreshing}
        onPrevious={() => applyState({ ...applied, page: applied.page - 1 })}
        onNext={() => applyState({ ...applied, page: applied.page + 1 })}
        onPageSizeChange={(pageSize) => applyState({ ...applied, page_size: pageSize, page: 1 })}
        pageSizeAriaLabel="Quotations per page"
        testId="quotation-pagination"
        summaryTestId="quotation-result-summary"
        pageTestId="quotation-page-summary"
        previousTestId="quotation-previous-page"
        nextTestId="quotation-next-page"
      />
    </ListTableShell>
    <QuotationLifecycleDialog open={Boolean(deleteTarget)} quotation={deleteTarget} error={deleteError} onClose={() => { setDeleteTarget(null); setDeleteError(""); }} onConfirm={confirmDelete} />
  </OrdinaryPageBody>;
}
